import React, { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../../services/aaiApi';
import { useAsync } from '../../../pages/aai/useAsync';
import type { ConnectorCatalog, ConnectorRow } from '../../../types/aai';
import { Action, ErrorNote, Loading, RouteMark } from '../../aa';
import { appIconUrl } from '../../dossier/AppIcon';
import { usePersonalConnect } from '../PersonalConnect';
import { TurnOffAppDialog } from './AppAccess';
import { ConnectorDetail } from './ConnectorDetail';
import { ConnectorsTable, isYours, rowsOf, searchText, type PanelMode } from './ConnectorsTable';

type ComposioHit = { slug: string; label: string; logo: string };

/* Composio's search overlaps our own catalog, so ask for enough that the apps
   we do not carry still show after those are filtered out. */
const COMPOSIO_SEARCH_LIMIT = 20;

/* Our catalog spells google_drive where Composio spells googledrive. */
const bare = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const AppMark: React.FC<{ slug: string; label: string; logo?: string }> = ({ slug, label, logo }) => {
  const [failed, setFailed] = useState(false);
  const url = logo || appIconUrl(slug);
  if (url && !failed) {
    return (
      <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-tile border border-line bg-white">
        <img src={url} alt="" width={18} height={18} className="object-contain" onError={() => setFailed(true)} />
      </span>
    );
  }
  return <RouteMark glyph={label.charAt(0).toUpperCase()} />;
};

export const ConnectorsPanel: React.FC<{ mode: PanelMode; initialTool?: string | null }> = ({ mode, initialTool = null }) => {
  const catalog = useAsync<ConnectorCatalog>(() => aaiApi.getConnectors(), 'Could not load the app catalog.');
  const [query, setQuery] = useState('');
  const [openSlug, setOpenSlug] = useState<string | null>(initialTool);
  const [found, setFound] = useState<ComposioHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [addingSlug, setAddingSlug] = useState('');
  const [turningOff, setTurningOff] = useState<ComposioHit | null>(null);
  const personal = usePersonalConnect(catalog.reload);

  const rows = useMemo(() => (catalog.data ? rowsOf(catalog.data, mode) : []), [catalog.data, mode]);
  const needle = query.trim().toLowerCase();
  const shown = useMemo(() => rows
    .filter((r) => !needle || searchText(r).toLowerCase().includes(needle))
    .sort((a, b) => Number(isYours(b)) - Number(isYours(a)) || a.label.localeCompare(b.label)), [rows, needle]);

  /* A beat behind the typing, the same pace as the tools card's search. Every
     role searches every app Composio offers and connects what it finds. */
  useEffect(() => {
    if (needle.length < 2) {
      setFound([]);
      setSearching(false);
      return undefined;
    }
    let live = true;
    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const res = await aaiApi.searchTools(needle, COMPOSIO_SEARCH_LIMIT);
        if (live) setFound(res.data.tools || []);
      } catch {
        if (live) setFound([]);
      } finally {
        if (live) setSearching(false);
      }
    }, 350);
    return () => { live = false; window.clearTimeout(timer); };
  }, [needle]);

  if (catalog.loading) return <Loading label="Loading apps" />;
  if (catalog.error || !catalog.data) return <ErrorNote message={catalog.error || 'Something went wrong.'} />;
  const data = catalog.data;
  const canSwitch = mode === 'owner' && Boolean(data.can_switch_company);

  /* The app joins the company's list, which is what lets the Owner see it and
     turn it off, then the person connects their own account. */
  const connectFound = async (hit: ComposioHit) => {
    setAddingSlug(hit.slug);
    try {
      const res = await aaiApi.addCompanyTool({ slug: hit.slug, label: hit.label, logo: hit.logo });
      const tool = res.data.tool;
      setQuery('');
      catalog.reload();
      await personal.connect({
        slug: tool.slug, label: tool.label || hit.label, category: 'added', category_label: '', enabled: true,
        connect_here: true, setup_hint: null, personal_status: null, organization_status: null, logo: hit.logo,
      } as ConnectorRow);
    } catch (err) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || `Could not add ${hit.label}.`);
    } finally {
      setAddingSlug('');
    }
  };

  const handlers = {
    mode,
    busy: personal.busy,
    onConnect: personal.connect,
  };
  const open = rows.find((r) => r.slug === openSlug) || null;
  if (open) return <ConnectorDetail row={open} catalog={data} mode={mode} handlers={handlers} onBack={() => setOpenSlug(null)} onChanged={catalog.reload} />;

  let emptyTitle = 'No apps in the catalog';
  let emptyReason = 'Tools the company can connect appear here.';
  if (needle) { emptyTitle = `Nothing matches "${query}"`; emptyReason = 'Try a different name.'; }

  const known = new Set(data.connectors.flatMap((c) => [bare(c.slug), bare(c.label)]));
  const more = found.filter((hit) => !known.has(bare(hit.slug)) && !known.has(bare(hit.label)));
  const showTable = shown.length > 0 || (more.length === 0 && !searching);

  return (
    <div className="mx-auto max-w-[1000px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-[13px] text-ink-muted">{rows.length} connectors</span>
        <span className="flex flex-1 items-center justify-end gap-2">
          <span className="relative w-[240px] max-w-full">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search every app"
              aria-label="Search connectors"
              className="h-10 w-full rounded-act border border-line bg-white pl-9 pr-3 text-[13px] text-ink outline-none placeholder:text-ink-soft focus:border-ink"
            />
          </span>
        </span>
      </div>
      {showTable && (
        <div className="mt-4">
          <ConnectorsTable rows={shown} catalog={data} handlers={handlers} onOpen={(r) => setOpenSlug(r.slug)} emptyTitle={emptyTitle} emptyReason={emptyReason} onChanged={catalog.reload} />
        </div>
      )}
      {searching && more.length === 0 && <p className="m-0 mt-4 text-[13px] text-ink-soft">Searching more apps…</p>}
      {more.length > 0 && (
        <section className="mt-6">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft">More apps</div>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {more.map((hit) => (
              <div key={hit.slug} className="flex items-center gap-3 rounded-act border border-line bg-white px-3.5 py-3">
                <AppMark slug={hit.slug} label={hit.label} logo={hit.logo} />
                <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-ink">{hit.label}</span>
                {canSwitch && <Action size="sm" variant="ghost" disabled={addingSlug !== ''} onClick={() => setTurningOff(hit)}>Turn off</Action>}
                {data.can_use && <Action size="sm" variant="quiet" disabled={addingSlug !== ''} reason="Connecting" onClick={() => connectFound(hit)}>Connect</Action>}
              </div>
            ))}
          </div>
        </section>
      )}
      {turningOff && (
        <TurnOffAppDialog
          target={turningOff}
          onClose={() => setTurningOff(null)}
          onSaved={() => { setTurningOff(null); setQuery(''); catalog.reload(); }}
        />
      )}
    </div>
  );
};

export default ConnectorsPanel;
